import { useState } from 'react';
import styled from '@emotion/styled';
import { m } from 'framer-motion';

import { screen } from '@/components/common/breakpoints';
import { Button } from '@/components/common/Buttons';
import { Divider } from '@/components/common/Divider';
import { Image } from '@/components/common/image';
import { Surface } from '@/components/common/Surface';
import { Stack } from '@/components/layout/Stack';
import { KeyFacts } from '@/components/page/case-study/KeyFacts';
import { KeyStats } from '@/components/page/case-study/KeyStats';
import { ProgrammeLinks } from '@/components/page/case-study/ProgrammeLinks';
import { useMediaQuery } from '@/lib/useMediaQuery';

import type { ProgrammeLinksProp } from '@/components/page/case-study/ProgrammeLinks';
import type { SanityCaseStudyType, SanitySidePanelType } from '@/types/sanity';

export interface SidebarProps {
  image?: SanitySidePanelType['image'];
  keyStats?: SanitySidePanelType['keyStats'];
  programmes?: ProgrammeLinksProp['programmes'];
  keyFacts?: SanityCaseStudyType['keyFacts'];
}

export const Sidebar = ({
  image,
  keyStats,
  programmes,
  keyFacts,
}: SidebarProps) => {
  const [showKeyFacts, setShowKeyFacts] = useState(false);

  const keyFactsHandler = () => {
    setShowKeyFacts((value) => !value);
  };

  const isDesktop = useMediaQuery(screen.lgQuery);
  return (
    <SidebarContainer
      surfaceColour="--surface-warm-base"
      borderRadius="--radius-component-xl"
      padding={false}
    >
      {image?.asset && (
        <CreativeContainer>
          <Image {...image} objectFit="cover" alt="" />
        </CreativeContainer>
      )}
      <Content
        spacing={{
          base: showKeyFacts ? '--space-component-lg' : 0,
          lg: '--space-component-lg',
        }}
      >
        <KeyStats keyStats={keyStats} />
        {isDesktop && (
          <>
            <Divider orientation="horizontal" colour="--border-warm-base" />
            <ProgrammeLinks programmes={programmes} />
          </>
        )}

        <m.div
          initial="hide"
          animate={showKeyFacts ? 'show' : 'hide'}
          variants={{
            show: { opacity: 1, height: 'auto', visibility: 'visible' },
            hide: {
              opacity: 0,
              height: 0,
            },
            transitionEnd: { visibility: 'hidden' },
          }}
          transition={{
            duration: 0.3,
          }}
        >
          <Stack spacing="--space-component-lg">
            <KeyFacts {...keyFacts} />
            <Divider orientation="horizontal" colour="--border-warm-base" />
            <ProgrammeLinks programmes={programmes} />
          </Stack>
        </m.div>

        {keyFacts ? (
          <MobileContentContainer showKeyFacts={showKeyFacts}>
            <StyledButton
              variant="flat-light"
              icon="ChevronDown"
              size="lg"
              onClick={keyFactsHandler}
              iconRotation={showKeyFacts ? '180' : '0'}
            >
              Key facts
            </StyledButton>
          </MobileContentContainer>
        ) : null}
      </Content>
    </SidebarContainer>
  );
};

const SidebarContainer = styled(Surface)`
  position: relative;
  height: max-content;
  top: 0;
  margin-top: var(--space-block-lg);

  ${screen.lg} {
    position: sticky;
    margin: 0 0 var(--space-component-sm) 0;
  }
`;

const CreativeContainer = styled.div`
  height: 144px;
  padding: var(--space-component-sm);
  display: flex;
  align-items: center;
  > img {
    height: 100%;
    border-radius: var(--radius-component-md);
  }
`;

const Content = styled(Stack)`
  padding-top: var(--space-component-md);
  padding-right: var(--space-component-xl);
  padding-bottom: var(--space-component-xl);
  padding-left: var(--space-component-xl);

  ${screen.lg} {
    padding-top: var(--space-component-lg);
  }
  transition: gap 0.3s;
`;

const StyledButton = styled(Button)`
  align-self: flex-end;
`;

const MobileContentContainer = styled.div<{ showKeyFacts: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: ${({ showKeyFacts }) =>
    showKeyFacts ? '0' : 'var(--space-component-lg)'};
  ${screen.lg} {
    display: none;
  }
  transition: margin 0.3s;
`;
